import React from "react"
import { useSelector } from "react-redux"

import { WORKFLOW_LEVEL_ASSIGNED } from "const"
import {
    CcComplianceHistory,
    CcReviewComplianceHistory,
    CcReviewReviewHistory,
    CcRequirementHistory,
} from "server/models"
import {
    StoreType,
    UserList,
    CcApprovalLevelList,
    CcResponseList,
    LangType,
} from "client/store"
import {
    DateFormatter,
    Button,
} from "client/components"
import {
    HistoryListWrapper,
    HistoryWrapper,
    History,
    TitleRow,
    ButtonRow,
    DraftSaved,
} from "./styled"

import { HistoryTitle, HistoryTitlePdfExport } from "./title"
import { HistoryAnswer, HistoryAnswerPdfExport } from "./answer"
import { HistoryResponse, HistoryResponsePdfExport } from "./response"
import { HistoryRemark, HistoryRemarkPdfExport } from "./remark"
import { HistoryNextRevision, HistoryNextRevisionPdfExport } from "./nextRevision"
import { HistoryAttachment, HistoryAttachmentPdfExport } from "./attachment"
import { HistoryStatus, HistoryStatusPdfExport } from "./status"
import { HistoryWorkflow, HistoryWorkflowPdfExport } from "./workflow"

export type ComplianceHistory = CcComplianceHistory | CcReviewComplianceHistory
export type HistoryType = ComplianceHistory | CcReviewReviewHistory | CcRequirementHistory

export interface NestedHistory {
    history: HistoryType
    historyNested: NestedHistory[]
}
export interface HistoryListProps {
    nested: NestedHistory[]
    onEditAnswer?: (history: HistoryType) => void
}
export interface HistoryProps {
    history: HistoryType
}


export const PrepareNestedHistory = (history: readonly HistoryType[]) => {
    const nested: { [historyId: number]: NestedHistory } = {}

    nested[0] = {
        history: null,
        historyNested: [],
    }

    history.forEach((row) => {
        const historyId = row.id
        nested[historyId] = {
            history: row,
            historyNested: [],
        }

        const parentId = row.parentId === null ? 0 : row.parentId
        if (nested[parentId] && nested[historyId]) {
            nested[parentId].historyNested.push(nested[historyId])
        }
    })

    return nested[0].historyNested
}


export const HistoryList = ({
    nested,
    onEditAnswer = null,
}: HistoryListProps) => {
    const userData = useSelector((state: StoreType) => state.system.userData)
    const lang = useSelector((state: StoreType) => state.system.lang)

    return (
        <HistoryListWrapper>
            {
                nested.map((row) => {
                    const { history, historyNested } = row
                    if ( // hide system messages where level was removed
                        history.systemMessage
                        && history.workflow === WORKFLOW_LEVEL_ASSIGNED
                        && !history.prevLevelId
                    ) return null

                    return (
                        <HistoryWrapper key={history.id}>
                            <History>
                                <TitleRow>
                                    <HistoryTitle history={history} />
                                    {
                                        history.id > 0 && <DateFormatter date={history.createdAt} defaultStyles />
                                    }
                                    {
                                        history.id < 0 && <DraftSaved>{lang.draftSaved}</DraftSaved>
                                    }
                                </TitleRow>

                                <HistoryAnswer history={history} />
                                <HistoryResponse history={history} />
                                <HistoryRemark history={history} />
                                <HistoryNextRevision history={history} />
                                <HistoryAttachment history={history} />
                                <HistoryStatus history={history} />
                                <HistoryWorkflow history={history} />

                                {
                                    userData.accountId === 1
                                    && !history.systemMessage
                                    && (
                                        <ButtonRow>
                                            <Button onClick={() => onEditAnswer(history)}>
                                                {lang.edit}
                                            </Button>
                                        </ButtonRow>
                                    )
                                }
                            </History>

                            {
                                historyNested.length > 0 && (
                                    <HistoryList nested={historyNested} onEditAnswer={onEditAnswer} />
                                )
                            }
                        </HistoryWrapper>
                    )
                })
            }
        </HistoryListWrapper>
    )
}


export const HistoryListPdfExport = (
    nested: NestedHistory[],
    userList: UserList,
    levelList: CcApprovalLevelList,
    responseList: CcResponseList,
    lang: LangType,
    nestedLevel = 1,
) => {
    const stack: any[] = []
    nested.forEach((row) => {
        const { history, historyNested } = row
        if ( // hide system messages where level was removed
            history.systemMessage
            && history.workflow === WORKFLOW_LEVEL_ASSIGNED
            && !history.prevLevelId
        ) return

        const tableBody: any[][] = [
            [HistoryTitlePdfExport(history, userList, levelList, lang)],
        ]

        const answer = HistoryAnswerPdfExport(history, lang)
        if (answer) tableBody.push([answer])

        const response = HistoryResponsePdfExport(history, responseList, lang)
        if (response) tableBody.push([response])

        const remark = HistoryRemarkPdfExport(history, lang)
        if (remark) tableBody.push([remark])

        const nextRevision = HistoryNextRevisionPdfExport(history, lang)
        if (nextRevision) tableBody.push([nextRevision])

        const attachment = HistoryAttachmentPdfExport(history, lang)
        if (attachment) tableBody.push([attachment])

        const status = HistoryStatusPdfExport(history, responseList, lang)
        if (status) tableBody.push([status])

        const workflow = HistoryWorkflowPdfExport(history, userList, levelList, responseList, lang)
        if (workflow) tableBody.push([workflow])

        stack.push({
            layout: {
                hLineWidth: (i: number, node: any) => ((i === 0 || i === node.table.body.length) ? 1 : 0),
                vLineWidth: () => 1,
                paddingLeft: () => 6,
                paddingRight: () => 6,
                paddingTop: (i: number) => (i === 0 ? 6 : 2),
                paddingBottom: (i: number, node: any) => ((i === node.table.body.length - 1) ? 6 : 2),
            },
            table: {
                widths: ["*"],
                body: tableBody,
            },
            margin: [nestedLevel * 20, 5, 0, 5],
        })

        if (historyNested.length > 0) {
            stack.push({
                stack: HistoryListPdfExport(historyNested, userList, levelList, responseList, lang, nestedLevel + 1),
            })
        }
    })

    return stack
}
